import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { autoScheduleApi, type CleanerProposal } from '../../api/availability.ts';
import { useSession } from '../../state/session.tsx';
import { Button } from '../ui.tsx';
import { formatLocalTime, formatNumericDate } from '../../i18n/datetime.ts';
import { KM_PER_MILE, usesMiles } from '../../../shared/cleaner-scheduling.ts';

/**
 * Auto-scheduling for a sold quote: the top three cleaner-and-slot pairs,
 * scored on skill, drive from the previous job and room under the daily cap.
 * Picking one fills the form; nothing is booked or assigned until the
 * dispatcher saves it.
 */
export function CleanerSuggestions({
  quoteId,
  from,
  durationMinutes,
  frequency,
  propertyId,
  enabled = true,
  onPick,
}: {
  quoteId: string;
  /** The address picked on the form: distances are measured to it. */
  propertyId?: string;
  from: string;
  durationMinutes: number;
  frequency: string;
  enabled?: boolean;
  onPick: (proposal: CleanerProposal) => void;
}) {
  const { t } = useTranslation();
  const { company, currentLocation } = useSession();
  const miles = usesMiles(company?.countryCode);
  const query = useQuery({
    queryKey: [
      'cleaner-suggestions',
      company?.id,
      currentLocation?.id,
      quoteId,
      from,
      durationMinutes,
      frequency,
      propertyId ?? null,
    ],
    queryFn: () =>
      autoScheduleApi.suggestions({ quoteId, from, durationMinutes, frequency, ...(propertyId ? { propertyId } : {}) }),
    enabled: Boolean(currentLocation) && durationMinutes >= 15,
    retry: false,
  });
  const distance = (km: number) =>
    miles
      ? t('autoSchedule.miles', { value: Math.round((km / KM_PER_MILE) * 10) / 10 })
      : t('autoSchedule.km', { value: km });
  return (
    <section className="my-3" aria-label={t('autoSchedule.title')}>
      <h3 className="field-label">{t('autoSchedule.title')}</h3>
      <p className="field-hint">{t('autoSchedule.hint')}</p>
      {query.isPending ? (
        <p>{t('suggestions.loading')}</p>
      ) : query.isError ? (
        <Button onClick={() => void query.refetch()}>{t('planning.retry')}</Button>
      ) : (
        <>
          {query.data.requiredSkill && (
            <p className="text-sm">
              {t('autoSchedule.needs', { skill: t(`autoSchedule.skill.${query.data.requiredSkill}`) })}
            </p>
          )}
          {query.data.requiredJobSkills.length > 0 && (
            <p className="text-sm">
              {t('autoSchedule.needsJob', {
                skills: query.data.requiredJobSkills.map((s) => t(`autoSchedule.skill.${s}`)).join(', '),
              })}
            </p>
          )}
          <ol className="grid gap-2">
            {query.data.items.map((item) => (
              <li key={`${item.userId}/${item.startsOn}/${item.startTimeLocal}`}>
                <Button className="w-full text-left" disabled={!enabled} onClick={() => onPick(item)}>
                  <span className="block font-semibold">
                    {t('autoSchedule.pick', {
                      name: item.name,
                      date: formatNumericDate(item.startsOn),
                      time: formatLocalTime(item.startTimeLocal),
                    })}
                  </span>
                  <span className="block text-xs">
                    {item.travelFrom === 'none'
                      ? t('autoSchedule.firstJob')
                      : item.kmFromPreviousJob === null
                        ? t(item.travelFrom === 'home' ? 'autoSchedule.homeUnknown' : 'autoSchedule.distanceUnknown')
                        : t(item.travelFrom === 'home' ? 'autoSchedule.fromHome' : 'autoSchedule.fromPrevious', {
                            distance: distance(item.kmFromPreviousJob),
                          })}
                    {' · '}
                    {t('autoSchedule.jobsThatDay', { count: item.jobsThatDay + 1 })}
                    {item.occurrencesChecked > 1 &&
                      ` · ${t('autoSchedule.weeksClear', {
                        clear: item.occurrencesChecked - item.occurrencesClashing,
                        total: item.occurrencesChecked,
                      })}`}
                  </span>
                  {item.flags.includes('missing_job_skill') && (
                    <span className="block text-xs">{t('autoSchedule.missingJobSkill')}</span>
                  )}
                </Button>
              </li>
            ))}
          </ol>
          {query.data.items.length === 0 && <p>{t('autoSchedule.none')}</p>}
          {query.data.reservedWeeks !== null && query.data.items.length > 0 && (
            <p className="field-hint">{t('autoSchedule.reserved', { weeks: query.data.reservedWeeks })}</p>
          )}
          {query.data.uncertain && <p role="status">{t('autoSchedule.uncertain')}</p>}
        </>
      )}
    </section>
  );
}
